import { Grid, UI, Utils as UtilsModule } from 'common';
import UserCapsEnum from 'common/src/code/utils/UserCaps.enum';
import UserRoleEnum from 'common/src/code/utils/UserRole.enum';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import { User } from './User';
import UserActionType from './UserActionType.enum';
import Utils from './Utils';

const { Util, Session } = UtilsModule;
const { GridPanel } = Grid;
const { Tag } = UI;

const StatusTagCellRenderer = ({ value }) =>
	value ? (
		<Tag
			value={value}
			status={value === 'Active' ? 'success' : 'warning'}
			size="condensed"
		/>
	) : null;
const {
	ucPurchasePostage,
	ucWebBasedReports,
	ucAdvancedReporting,
	ucPurchaseFromStore,
	ucReferenceCodeOptional,
	ucEditReferenceCodes,
} = UserCapsEnum;

const userCapsColumns = [
	{ headerName: 'Add Funds', width: 100, field: 'AddFunds' },
	{ headerName: 'View Reports', width: 120, field: 'ViewReports' },
	{ headerName: 'Buy Supplies', width: 120, field: 'BuySupplies' },
	{ headerName: 'Cost Codes Required', width: 150, field: 'CostCodesRequired' },
	{ headerName: 'Edit Cost Codes', width: 150, field: 'EditCostCodes' },
];

const getColumnDefs = enterpriseLocation => {
	const userRole = Session.getUserRole();
	const isEnterpriseAccount =
		!enterpriseLocation && Utils.checkIsEnterpriseAdmin(userRole);

	const ucValueColumns = userCapsColumns.map(column => ({
		...column,
		valueFormatter: ({ value }) => {
			if (value === undefined) {
				return '';
			}
			return value ? 'Yes' : 'No';
		},
	}));

	const enterpriseColumn = isEnterpriseAccount
		? [
				{
					headerName: 'Location',
					width: 150,
					field: 'Location',
				},
			]
		: [];
	// WEBAPPS-32134, Removed hasCanManageRegions() due to CanManageRegions appcap was never actually implemented in v1
	if (userRole === UserRoleEnum.CorpAdmin && !enterpriseLocation) {
		enterpriseColumn.push({
			headerName: 'Region',
			width: 150,
			field: 'Region',
		});
	}

	return [
		{
			headerName: 'Name',
			field: 'FullName',
			checkboxSelection: true,
			headerCheckboxSelection: true,
			sort: 'asc',
			flex: 1,
			minWidth: 200,
		},
		{
			headerName: 'Role',
			width: 150,
			field: 'RoleLabel',
		},
		...enterpriseColumn,
		{
			headerName: 'Status',
			width: 100,
			field: 'Status',
			cellRenderer: 'StatusTagCellRenderer',
		},
		{
			headerName: 'Last Active',
			width: 150,
			field: 'LastActiveDate',
			comparator: Util.dateComparator,
		},
		{ headerName: 'Per-Print Limit', width: 120, field: 'PerPrintLimit' },
		...ucValueColumns,
	];
};

const getPerPrintLimit = UserCapabilities => {
	// Use JavaScript Intl.NumberFormat to format per-print limit value
	const usCurrencyFormatter = new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
	});
	if (
		UserCapabilities.PerPrintMaximumAmountLimit === Utils.MAXIMUM_LIMIT_AMOUNT
	) {
		return 'No Limit';
	} else if (UserCapabilities.PerPrintMaximumAmountLimit === 0) {
		return '$0.00';
	} else if (UserCapabilities.PerPrintMaximumAmountLimit > 0) {
		return usCurrencyFormatter.format(
			UserCapabilities.PerPrintMaximumAmountLimit
		);
	}
	return 'N/A';
};

const getUsersGridData = (users = []) => {
	const isEnterpriseAccount = Utils.checkIsEnterpriseAdmin(
		Session.getUserRole()
	);

	return users.map(user => {
		if (isEmpty(user)) {
			return new User();
		}

		const {
			IsUserActivated,
			FirstName,
			LastName,
			UserCapabilities,
			LastActiveDate,
		} = user;
		const { Capabilities = 0 } = UserCapabilities || {};
		const userModel = new User(user);
		const parentCorpID =
			user &&
			user.UsersCorporationInformation &&
			user.UsersCorporationInformation.ParentCorpId;
		userModel.Role = Utils.getRoleUser(user).value;
		userModel.RoleLabel = Utils.getRoleUser(user).label;
		userModel.Status = IsUserActivated ? 'Active' : 'Invited';
		userModel.FullName =
			FirstName && LastName ? `${FirstName} ${LastName}` : '';
		userModel.PerPrintLimit = UserCapabilities
			? getPerPrintLimit(UserCapabilities)
			: 'N/A';
		userModel.AddFunds = Util.hasBitSet(Capabilities, ucPurchasePostage);
		userModel.ViewReports = Util.hasBitSet(
			Capabilities,
			ucWebBasedReports || ucAdvancedReporting
		);
		userModel.BuySupplies = Util.hasBitSet(Capabilities, ucPurchaseFromStore);
		userModel.CostCodesRequired = !Util.hasBitSet(
			Capabilities,
			ucReferenceCodeOptional
		);
		userModel.EditCostCodes = Util.hasBitSet(
			Capabilities,
			ucEditReferenceCodes
		);
		userModel.LastActiveDate = LastActiveDate;

		if (isEnterpriseAccount) {
			userModel.UsersCorporationInformation = user.UsersCorporationInformation;
			userModel.UserCorporateCapabilities = user.UserCorporateCapabilities;
			// location
			userModel.Location = user.MeterAccount.Name || '';
			// region
			userModel.Region = parentCorpID
				? user.UsersCorporationInformation.CorpName
				: '';
		}

		return userModel;
	});
};

const UsersGrid = props => {
	const {
		users = [],
		action,
		onSelectionChanged,
		gridApi,
		onGridReady,
		handleSortUserGrid,
		regions,
		locations,
		enterpriseLocation,
	} = props;

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(100);
	const [gridData, setGridData] = useState(users);
	const totalPages = Math.ceil(users.length / pageSize) || 1;

	useEffect(() => {
		const nextPage =
			action === UserActionType.ADD_NEW_USER ? totalPages : currentPage;
		const startIndexSlice = (nextPage - 1) * pageSize;
		const endIndexSlice = nextPage * pageSize;

		setCurrentPage(nextPage);
		setGridData(users.slice(startIndexSlice, endIndexSlice));

		if (!gridApi) {
			return;
		}

		gridApi.forEachNode(node => {
			if (isEmpty(node.data)) {
				node.setSelected(true);
			}
		});
	}, [users, gridApi, currentPage, action, totalPages, pageSize]);

	const fetchUsersDataByPage = (nextPage, nextPageSize) => {
		const startIndexSlice = nextPage === 1 ? 0 : (nextPage - 1) * nextPageSize;
		const endIndexSlice = nextPage * nextPageSize;

		setGridData(gridData.slice(startIndexSlice, endIndexSlice));
		setCurrentPage(nextPage);
		setPageSize(nextPageSize);

		if (gridApi) {
			onSelectionChanged();
		}
	};

	return (
		<GridPanel
			className="account-users-users-grid"
			defaultColDef={{ suppressSizeToFit: true }}
			frameworkComponents={{ StatusTagCellRenderer }}
			rowData={getUsersGridData(gridData, locations, regions)}
			showPagination
			columnDefs={getColumnDefs(enterpriseLocation)}
			onSortChanged={handleSortUserGrid}
			currentPage={currentPage}
			totalPages={totalPages}
			pageSize={pageSize}
			onGridReady={onGridReady}
			rowSelection="multiple"
			fetchData={fetchUsersDataByPage}
			onSelectionChanged={onSelectionChanged}
			dataqa="account-users-users-grid"
		/>
	);
};

export default UsersGrid;
